<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[10003] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
  >
    <div
      class="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
    >
      <!-- Header -->
      <div class="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
        <h3 class="text-lg font-bold text-gray-800">Generate QR Codes</h3>
        <button
          @click="handleClose"
          class="p-2 hover:bg-gray-200 rounded-full transition-colors"
          aria-label="Close"
        >
          <svg class="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Settings bar -->
      <div class="qr-settings">
        <label class="qr-field">
          <span>Symbology</span>
          <select v-model="settings.symbology">
            <option v-for="(lbl, val) in symbologyLabels" :key="val" :value="val">{{ lbl }}</option>
          </select>
        </label>
        <label class="qr-field">
          <span>Encode mode</span>
          <select v-model="settings.encodeMode" :disabled="is1D(settings.symbology)">
            <option v-for="(lbl, val) in encodeModeLabels" :key="val" :value="val">{{ lbl }}</option>
          </select>
        </label>
        <label class="qr-field">
          <span>Error correction</span>
          <select v-model="settings.ecc" :disabled="settings.symbology !== 'qr' && settings.symbology !== 'microqr'">
            <option value="L">L (smallest)</option>
            <option value="M">M</option>
            <option value="Q">Q</option>
            <option value="H">H</option>
          </select>
        </label>
        <label class="qr-field">
          <span>Print size (mm)</span>
          <input v-model.number="settings.printSizeMm" type="number" min="3" max="80" step="1" />
        </label>
        <label class="qr-field">
          <span>Dot colour</span>
          <input v-model="settings.dotColor" type="text" />
        </label>
        <label class="qr-field">
          <span>Label size (mm)</span>
          <input :value="`${settings.labelWidthMm} × ${settings.labelHeightMm}`" type="text" disabled />
        </label>
        <div class="qr-toggle">
          <input id="qr-showlabel" v-model="settings.showLabelText" type="checkbox" />
          <label for="qr-showlabel">Show label text</label>
        </div>
      </div>

      <!-- Field picker: which item fields the "Order / SKU / Size" code encodes -->
      <div v-if="settings.encodeMode === 'fields' && !is1D(settings.symbology)" class="qr-fieldpick">
        <span class="qr-fieldpick-title">Include in code</span>
        <label class="qr-fieldpick-opt">
          <input v-model="settings.includeOrder" type="checkbox" />
          <span>Order</span>
        </label>
        <label class="qr-fieldpick-opt">
          <input v-model="settings.includeSku" type="checkbox" />
          <span>SKU</span>
        </label>
        <label class="qr-fieldpick-opt">
          <input v-model="settings.includeSize" type="checkbox" />
          <span>Size</span>
        </label>
      </div>

      <!-- Readability meter -->
      <div class="qr-meter" :class="`qr-meter--${verdict.level}`">
        <span class="font-extrabold">{{ verdict.text }}</span>
        <span class="text-gray-600">{{ verdict.detail }}</span>
      </div>
      <div v-if="compatNote" class="qr-compat">{{ compatNote }}</div>

      <!-- Label grid -->
      <div class="flex-1 overflow-y-auto bg-gray-100 px-5 py-4">
        <div v-if="isLoading" class="flex items-center justify-center py-12">
          <div class="animate-spin rounded-full h-10 w-10 border-4 border-[#16b79a] border-t-transparent"></div>
        </div>
        <div
          v-else-if="expandedItems.length > 0"
          id="qr-print-area"
          class="grid grid-cols-2 md:grid-cols-3 gap-4 print:grid-cols-1"
        >
          <div
            v-for="(item, idx) in expandedItems"
            :key="`${idx}-${item.code}`"
            class="qr-card break-inside-avoid"
          >
            <div v-if="settings.showLabelText" class="qr-card-meta">
              <span v-if="item.product_name" class="block">ITEM: {{ item.product_name }}</span>
              <span v-if="item.player_name" class="block">PLAYER: {{ item.player_name }}</span>
              <span v-if="item.player_number || item.size" class="block">
                <template v-if="item.player_number">NO: {{ item.player_number }}&nbsp;&nbsp;</template>
                <template v-if="item.size">SIZE: {{ item.size }}</template>
              </span>
              <span v-if="item.page_reference || item.code" class="block text-gray-400">
                <template v-if="item.page_reference">PAGE: {{ item.page_reference }} · </template>{{ item.code }}
              </span>
            </div>
            <div class="qr-symbol-col flex flex-col items-center gap-1.5">
              <canvas :ref="(el) => registerCanvas(el, idx, 'main')" class="qr-canvas"></canvas>
              <span class="qr-code-text text-[11px] font-mono text-gray-600">{{ codeText(item) }}</span>
            </div>
          </div>
        </div>
        <div v-else class="flex items-center justify-center py-12 text-gray-500">
          <p>No roster items found for this order.</p>
        </div>
      </div>

      <!-- Footer -->
      <div class="p-4 border-t border-gray-100 bg-white flex flex-wrap justify-end gap-2.5">
        <button class="qr-btn qr-btn-ghost" @click="handleClose">Close</button>
        <button class="qr-btn qr-btn-soft" :disabled="!items.length || isExporting" @click="onDownloadPng">
          Download PNG
        </button>
        <button class="qr-btn qr-btn-soft" :disabled="!items.length || isExporting" @click="onDownloadAllSvg">
          Download all SVG
        </button>
        <button class="qr-btn qr-btn-soft" :disabled="!items.length || isExporting" @click="onExportExcel">
          Export Excel
        </button>
        <button class="qr-btn qr-btn-primary" :disabled="!items.length" @click="printLabels">
          Print / Save PDF
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { showToast } from '@/composables/swal';
import { http, handleResponseException } from '@/interceptors/axiosRequestInterceptor';
import type { ApiResponseV1, OrderForBarcode } from '@/models';
import {
  DEFAULT_QR_SETTINGS,
  ENCODE_MODE_LABELS,
  SYMBOLOGY_LABELS,
  type QrLabelItem,
  type QrSettings,
} from '@/models/QrLabelGenerator';
import {
  useQrLabels,
  is1D,
  buildPayload,
  computeReadability,
  expandByQuantity,
} from '@/composables/useQrLabels';

const props = defineProps<{
  show: boolean;
  order_no?: string;
}>();

const emit = defineEmits<{ close: [] }>();

const { renderToCanvas, downloadAllSvg, downloadPng, exportTemplate01Xlsx, normalizeRosterItem } =
  useQrLabels();

const symbologyLabels = SYMBOLOGY_LABELS;
const encodeModeLabels = ENCODE_MODE_LABELS;

const settings = reactive<QrSettings>({ ...DEFAULT_QR_SETTINGS });
const items = ref<QrLabelItem[]>([]);
const isLoading = ref(false);
const isExporting = ref(false);

/** Codes already used across the batch (keeps auto-generated codes unique). */
const takenCodes = new Set<string>();

/**
 * One physical label per unit of quantity — same expansion the barcode modal
 * does. Drives the on-screen grid, PNG/SVG downloads and print. The Excel
 * Template01 export stays one row per distinct item (it has a Quantity column).
 */
const expandedItems = computed(() => expandByQuantity(items.value));

/* --- canvas registry + rendering --- */
const canvasRefs = new Map<string, HTMLCanvasElement>();
function registerCanvas(el: unknown, idx: number, kind: 'main') {
  const key = `${idx}-${kind}`;
  if (el) canvasRefs.set(key, el as HTMLCanvasElement);
  else canvasRefs.delete(key);
}

async function renderAll() {
  await nextTick();
  const list = expandedItems.value;
  for (let idx = 0; idx < list.length; idx += 1) {
    const main = canvasRefs.get(`${idx}-main`);
    if (main) {
      try {
        await renderToCanvas(main, list[idx], settings);
      } catch {
        // Invalid payload for the chosen symbology (e.g. Code 39 + lowercase).
        const ctx = main.getContext('2d');
        if (ctx) {
          main.width = 120;
          main.height = 40;
          ctx.clearRect(0, 0, main.width, main.height);
          ctx.fillStyle = '#dc2626';
          ctx.font = '10px sans-serif';
          ctx.fillText('cannot encode', 6, 22);
        }
      }
    }
  }
}

/* --- data sources --- */
async function loadRoster(orderNo: string) {
  isLoading.value = true;
  items.value = [];
  takenCodes.clear();
  try {
    const response = await http.post<ApiResponseV1<OrderForBarcode>>(`order/${orderNo}/for/barcode`);
    const data = response.data.result?.barcode_data ?? [];
    items.value = data.map((entry) => normalizeRosterItem(entry, orderNo, takenCodes));
    if (!items.value.length) showToast('No roster items found for this order', 'error');
  } catch (error) {
    handleResponseException(error);
  } finally {
    isLoading.value = false;
  }
}

/* --- meter + compatibility note --- */
const verdict = computed(() => {
  const sample = items.value[0] ?? ({ code: 'SAMPLE-CODE' } as QrLabelItem);
  return computeReadability(settings, buildPayload(sample, settings));
});

const compatNote = computed(() => {
  if (settings.symbology === 'microqr' || settings.symbology === 'datamatrix') {
    return 'Scanner note: Micro QR / DataMatrix may not read on stock phone cameras — use the Custimoo app or Google Lens. A true ~5 mm fabric mark needs DataMatrix.';
  }
  if (is1D(settings.symbology)) {
    return '1D barcode carries the code only (encode mode ignored). Best for warehouse / packing scanners.';
  }
  return '';
});

function codeText(item: QrLabelItem): string {
  // For resolver and the Order / SKU / Size picker, show what is actually
  // encoded so the user can verify it; other modes just show the short code.
  if ((settings.encodeMode === 'resolver' || settings.encodeMode === 'fields') && !is1D(settings.symbology)) {
    return buildPayload(item, settings);
  }
  return item.code;
}

/* --- exports --- */
async function onDownloadPng() {
  isExporting.value = true;
  try {
    await downloadPng(expandedItems.value, settings);
  } finally {
    isExporting.value = false;
  }
}

async function onDownloadAllSvg() {
  isExporting.value = true;
  try {
    await downloadAllSvg(expandedItems.value, settings);
  } finally {
    isExporting.value = false;
  }
}

async function onExportExcel() {
  isExporting.value = true;
  try {
    // One row per physical label (expanded by quantity), matching the grid.
    const rows = expandedItems.value.map((item) => ({ ...item, quantity: 1 }));
    await exportTemplate01Xlsx(rows, settings);
    showToast('Excel exported', 'success');
  } catch (error) {
    handleResponseException(error);
  } finally {
    isExporting.value = false;
  }
}

async function printLabels() {
  const area = document.getElementById('qr-print-area');
  if (!area) return;
  const clone = area.cloneNode(true) as HTMLElement;
  // The symbol must print at a known physical size, not the canvas's intrinsic
  // pixel size (which maps to an arbitrary mm size at the printer's DPI). Cap it
  // so it always fits the fixed 50.8 × 38.1 mm label — narrower when the meta
  // text column shares the row, taller-limited when the symbol stands alone.
  const cap = settings.showLabelText ? 25 : 30;
  const symbolMm = Math.min(Math.max(settings.printSizeMm || 0, 3), cap);
  // A cloned <canvas> is blank — its bitmap isn't copied. Replace each canvas
  // in the clone with an <img> snapshot of the live canvas so QR codes print.
  const liveCanvases = area.querySelectorAll('canvas');
  const clonedCanvases = clone.querySelectorAll('canvas');
  const symbolImgs: HTMLImageElement[] = [];
  clonedCanvases.forEach((cloned, i) => {
    const live = liveCanvases[i];
    if (!live) return;
    const img = document.createElement('img');
    img.src = live.toDataURL('image/png');
    img.className = cloned.className;
    img.style.width = `${symbolMm}mm`;
    img.style.height = `${symbolMm}mm`;
    cloned.replaceWith(img);
    symbolImgs.push(img);
  });
  const container = document.createElement('div');
  container.id = 'qr-print-container';
  container.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:100%;';
  container.appendChild(clone);
  document.body.appendChild(container);

  // Every symbol must finish decoding BEFORE the print dialog opens. On a fixed
  // timeout only the first few data-URL images have painted, so the rest print
  // as blank label pages (the "only 2 QR codes printed" bug). Wait for them all.
  await Promise.all(
    symbolImgs.map(
      (img) =>
        new Promise<void>((resolve) => {
          const done = () => resolve();
          if (img.complete && img.naturalWidth > 0) {
            done();
            return;
          }
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        }),
    ),
  );
  // One more frame so the browser lays out the fully-decoded images.
  await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));

  window.print();
  setTimeout(() => document.body.removeChild(container), 1000);
}

const handleClose = () => emit('close');

/* --- reactivity --- */
// The modal is order-scoped: whenever it opens with an order number, load that
// order's roster and render one QR label per roster item.
watch(
  [() => props.show, () => props.order_no],
  ([show, orderNo]) => {
    if (show && orderNo) loadRoster(orderNo);
  },
  { immediate: true },
);

// Re-render symbols whenever the labels or the settings change.
watch([items, settings], renderAll, { deep: true });
</script>

<style scoped>
.qr-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}
.qr-field > span {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.qr-field input,
.qr-field select {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 7px 9px;
  font-size: 13px;
  color: #1f2937;
  background: #fff;
}
.qr-field input:focus,
.qr-field select:focus {
  outline: 2px solid rgba(22, 183, 154, 0.4);
  border-color: #16b79a;
}
.qr-field input:disabled,
.qr-field select:disabled {
  background: #f3f4f6;
  color: #9ca3af;
}

.qr-settings {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px 18px;
  padding: 16px 20px;
  border-top: 1px solid #f3f4f6;
  border-bottom: 1px solid #f3f4f6;
}
@media (max-width: 720px) {
  .qr-settings {
    grid-template-columns: repeat(2, 1fr);
  }
}
.qr-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: end;
  padding-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}
.qr-toggle input {
  accent-color: #16b79a;
  width: 16px;
  height: 16px;
}

.qr-fieldpick {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 18px;
  margin: 14px 20px 0;
  padding: 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
}
.qr-fieldpick-title {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.qr-fieldpick-opt {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
}
.qr-fieldpick-opt input {
  accent-color: #16b79a;
  width: 16px;
  height: 16px;
}

.qr-meter {
  margin: 14px 20px 0;
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 13px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
}
.qr-meter--ok {
  background: #f0fdf4;
  border-color: #bbf7d0;
}
.qr-meter--ok .font-extrabold {
  color: #16a34a;
}
.qr-meter--warn {
  background: #fffbeb;
  border-color: #fde68a;
}
.qr-meter--warn .font-extrabold {
  color: #f59e0b;
}
.qr-meter--bad {
  background: #fef2f2;
  border-color: #fecaca;
}
.qr-meter--bad .font-extrabold {
  color: #dc2626;
}

.qr-compat {
  margin: 12px 20px 0;
  font-size: 12px;
  color: #6b7280;
  background: #f9fafb;
  border: 1px dashed #e5e7eb;
  border-radius: 8px;
  padding: 9px 12px;
}

.qr-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.qr-card-meta {
  width: 100%;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  color: #374151;
}
.qr-canvas {
  image-rendering: pixelated;
  border-radius: 4px;
  max-width: 100%;
}

.qr-btn {
  font-size: 13px;
  font-weight: 600;
  padding: 9px 16px;
  border-radius: 9px;
  border: 1px solid transparent;
}
.qr-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.qr-btn-ghost {
  background: transparent;
  color: #4b5563;
}
.qr-btn-ghost:hover {
  background: #f3f4f6;
}
.qr-btn-soft {
  background: #fff;
  border-color: #e5e7eb;
  color: #374151;
}
.qr-btn-soft:hover:not(:disabled) {
  background: #f9fafb;
}
.qr-btn-primary {
  background: #2563eb;
  color: #fff;
}
.qr-btn-primary:hover:not(:disabled) {
  background: #1d4ed8;
}
</style>

<style>
@media print {
  @page {
    size: 50.8mm 38.1mm;
    margin: 0;
  }
  body > *:not(#qr-print-container) {
    display: none !important;
  }
  #qr-print-container {
    display: block !important;
    position: static !important;
    width: 100% !important;
  }
  #qr-print-area {
    display: block !important;
  }

  /* One label per page: text column on the left, symbol on the right. */
  #qr-print-area .qr-card {
    box-sizing: border-box !important;
    width: 50.8mm !important;
    height: 38.1mm !important;
    margin: 0 !important;
    padding: 2.4mm 2.8mm !important;
    gap: 2.6mm !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    background: #fff !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: center !important;
    page-break-after: always !important;
    break-after: page !important;
  }
  #qr-print-area .qr-card:last-child {
    page-break-after: auto !important;
    break-after: auto !important;
  }

  /* Meta text column — key facts, tidy hierarchy, never spills over the symbol. */
  #qr-print-area .qr-card-meta {
    flex: 1 1 auto !important;
    min-width: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 0.6mm !important;
    font-size: 6.6pt !important;
    line-height: 1.25 !important;
    color: #374151 !important;
  }
  #qr-print-area .qr-card-meta > span {
    display: block !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }
  #qr-print-area .qr-card-meta > span:first-child {
    font-size: 8.4pt !important;
    font-weight: 800 !important;
    line-height: 1.15 !important;
    color: #111827 !important;
    white-space: normal !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
  }
  #qr-print-area .qr-card-meta > span.text-gray-400 {
    font-size: 6pt !important;
    color: #6b7280 !important;
  }

  /* Symbol column — thin divider from the text, symbol at its physical size. */
  #qr-print-area .qr-symbol-col {
    flex: 0 0 auto !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    gap: 1mm !important;
  }
  #qr-print-area .qr-card-meta + .qr-symbol-col {
    padding-left: 2.6mm !important;
    border-left: 0.25mm solid #e5e7eb !important;
  }
  #qr-print-area .qr-symbol-col img.qr-canvas {
    display: block !important;
    image-rendering: pixelated !important;
    border-radius: 0 !important;
  }
  #qr-print-area .qr-symbol-col .qr-code-text {
    font-family: ui-monospace, "SFMono-Regular", Menlo, monospace !important;
    font-size: 6pt !important;
    font-weight: 700 !important;
    letter-spacing: 0.02em !important;
    line-height: 1 !important;
    color: #111827 !important;
    max-width: 100% !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>
